import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  connectWS,
  onMessage,
  subscribeToQuotes,
  subscribeToPremium,
  isQuoteFresh,
  validateTradeQuotes,
  onQuoteBatch,
} from "../services/wsService";
import { ensureUnlocked, playOrderFilled, playError } from "../services/sound";
import {
  TrendingUp,
  TrendingDown,
  Zap,
  Target,
  Activity,
  BarChart3,
  Settings,
  AlertTriangle,
  Wallet,
  Landmark,
  ShieldCheck,
  Unlock,
  Gauge,
  CreditCard,
  Ruler,
  Info,
} from "lucide-react";
import API from "../services/api";
import { formatAppTime } from "../utils/dateFormat";
import { normalizeTakeProfitSubmission } from "../utils/takeProfitSubmission";
import CustomSelect from "./CustomSelect";
import RadioButtonGroup from "./RadioButtonGroup";
import { useToast } from "./Toast";
import AppPageLoader from "./AppPageLoader";
import "../pages/admin/AdminShared.css";
import "../pages/admin/AdminBroadcast.css";
import "./TradeExecution.css";

export default function TradeExecution({
  accountSet,
  accountSets = [],
  selectedSetId = "",
  onAccountSetChange,
}) {
  const toast = useToast();
  const [executingTrades, setExecutingTrades] = useState(new Set());
  const executionLocks = useRef(new Set());
  const executionTimeouts = useRef(new Map());
  const marginRequestId = useRef(0);
  const marginDebounceRef = useRef(null);
  const slowAckTimerRef = useRef(null);

  const [executionState, setExecutionState] = useState({
    isExecuting: false,
    step: "",
    startTime: null,
    broker1Status: "idle",
    broker2Status: "idle",
  });
  const [selectedBroker1, setSelectedBroker1] = useState("");
  const [selectedBroker2, setSelectedBroker2] = useState("");
  const [direction, setDirection] = useState("Sell");
  const [volume, setVolume] = useState("");
  const [targetPremium, setTargetPremium] = useState("");
  const [takeProfit, setTakeProfit] = useState("");
  const [takeProfitMode, setTakeProfitMode] = useState("None");
  const [scalping, setScalping] = useState(true);
  const [pendingMode, setPendingMode] = useState("Classic Trigger");
  const [ladderGroupId, setLadderGroupId] = useState("");

  const [realTimeProfits, setRealTimeProfits] = useState({ broker1: 0, broker2: 0, total: 0 });
  const b1ProfitRef = useRef(new Map());
  const b2ProfitRef = useRef(new Map());

  const [latencyData, setLatencyData] = useState({
    broker1: {
      lastOrderLatency: null,
      averageLatency: null,
      lastUpdate: null,
    },
    broker2: {
      lastOrderLatency: null,
      averageLatency: null,
      lastUpdate: null,
    },
  });
  const [marginInfo, setMarginInfo] = useState({
    loading: false,
    error: "",
    broker1: null,
    broker2: null,
    marginInsufficient: false,
  });
  const [initialReady, setInitialReady] = useState(!accountSet);

  useEffect(() => {
    try {
      ensureUnlocked();
    } catch {
      void 0;
    }
    if (!accountSets.length) {
      toast.warning("No linked account sets found. Link an account set before placing trades.");
    }
  }, [accountSets.length, toast]);
  const [futureQuote, setFutureQuote] = useState(null);
  const [spotQuote, setSpotQuote] = useState(null);
  const [lockedFutureQuote, setLockedFutureQuote] = useState(null);
  const [lockedSpotQuote, setLockedSpotQuote] = useState(null);
  const [buyPremium, setBuyPremium] = useState(0);
  const [sellPremium, setSellPremium] = useState(0);
  const [premiumLive, setPremiumLive] = useState(false);
  const [collectorStale, setCollectorStale] = useState(false);
  const [displayQuoteSourceLabel, setDisplayQuoteSourceLabel] = useState("");
  const [quoteFeedStatus, setQuoteFeedStatus] = useState("");
  const [quotesValidForTrading, setQuotesValidForTrading] = useState(false);
  const lastCollectorQuoteAt = useRef(0);
  const marketPauseToastShownRef = useRef(false);
  const COLLECTOR_PREFER_MS = 3000;
  const activeAccountSetId = accountSet?.id || accountSet?._id;
  const symbolsLocked = Boolean(accountSet?.symbolsLocked);
  const lockedFutureSymbol = accountSet?.futureSymbol || "";
  const lockedSpotSymbol = accountSet?.spotSymbol || "";

  const normalizeQuoteSource = useCallback((source) => {
    const value = String(source || "").toLowerCase();
    if (!value) return "";
    if (value.includes("collector")) return "collector";
    if (value.includes("ws_quote") || value.includes("websocket") || value.startsWith("ws"))
      return "ws";
    if (value.includes("api") || value.includes("rest")) return "rest";
    if (
      value.includes("database") ||
      value.includes("ws-db") ||
      value.includes("db") ||
      value.includes("cache")
    )
      return "db";
    return value;
  }, []);
  const isCollectorAvailabilityNotice = (message) =>
    /collector (quotes|premium updates) unavailable/i.test(String(message || ""));
  const getCollectorAvailabilityLabel = (message) =>
    /no snapshot yet/i.test(String(message || ""))
      ? "Waiting for first quote snapshot"
      : "Market closed / stale quotes";
  const showMarketPauseWarning = useCallback(() => {
    if (marketPauseToastShownRef.current) return;
    marketPauseToastShownRef.current = true;
    toast.warning("Live prices are paused right now. Please wait for fresh quotes.");
  }, [toast]);
  const isMarketPausePayload = useCallback((payload) => {
    const responseData = payload?.response?.data;
    const message = String(
      payload?.message || payload?.data?.message || responseData?.message || "",
    ).toLowerCase();
    const reason = String(
      payload?.reason || payload?.data?.reason || responseData?.reason || "",
    ).toLowerCase();
    const marketClosed =
      payload?.marketClosed === true ||
      payload?.data?.marketClosed === true ||
      responseData?.marketClosed === true;

    return (
      marketClosed ||
      reason === "market_closed" ||
      reason === "stale_quotes" ||
      message.includes("tp blocked: stale quotes") ||
      message.includes("pending order blocked: stale quotes") ||
      message.includes("pending order realtime update blocked: stale quotes") ||
      message.includes("pre-execution premium check failed: stale quotes") ||
      message.includes("quotes are too stale for safe execution") ||
      message.includes("please wait for fresh quotes") ||
      message.includes("market likely closed") ||
      message.includes("premium updates are paused")
    );
  }, []);

  const buildQuoteSourceLabel = useCallback(
    (future, spot) => {
      const futureSrc = normalizeQuoteSource(future?.source);
      const spotSrc = normalizeQuoteSource(spot?.source);
      if (!futureSrc && !spotSrc) return "";
      if (futureSrc && spotSrc && futureSrc === spotSrc) return futureSrc;
      if (futureSrc && spotSrc) return `future:${futureSrc} spot:${spotSrc}`;
      return futureSrc || spotSrc;
    },
    [normalizeQuoteSource],
  );
  const deriveQuoteSourceLabel = useCallback(
    (payload) =>
      buildQuoteSourceLabel(payload?.futureQuote, payload?.spotQuote) ||
      normalizeQuoteSource(
        payload?.futureQuote?.source || payload?.spotQuote?.source || payload?.source,
      ),
    [buildQuoteSourceLabel, normalizeQuoteSource],
  );

  const validateTradeState = useCallback(
    (tradeKey) => {
      const isNotExecuting = !executingTrades.has(tradeKey);
      const isNotLocked = !executionLocks.current.has(tradeKey);

      return isNotExecuting && isNotLocked;
    },
    [executingTrades],
  );

  const preventDuplicateExecution = useCallback((tradeKey) => {
    if (executionLocks.current.has(tradeKey)) {
      return false;
    }

    executionLocks.current.add(tradeKey);

    setTimeout(() => {
      if (executionLocks.current.has(tradeKey)) {
        executionLocks.current.delete(tradeKey);
      }
    }, 60000);

    return true;
  }, []);

  const releaseExecutionLock = useCallback((tradeKey) => {
    executionLocks.current.delete(tradeKey);
  }, []);

  const clearExecutionTimeout = useCallback((tradeKey) => {
    const timeoutId = executionTimeouts.current.get(tradeKey);
    if (timeoutId) {
      clearTimeout(timeoutId);
      executionTimeouts.current.delete(tradeKey);
    }
  }, []);

  useEffect(() => {
    if (selectedSetId || accountSet?.id || accountSet?._id) {
      setFutureQuote(null);
      setSpotQuote(null);
      setLockedFutureQuote(null);
      setLockedSpotQuote(null);
      setDisplayQuoteSourceLabel("");
      setBuyPremium(0);
      setSellPremium(0);
      setPremiumLive(false);
      setCollectorStale(false);
      setQuoteFeedStatus("");
      setMarginInfo({
        loading: false,
        error: "",
        broker1: null,
        broker2: null,
        marginInsufficient: false,
      });
    }
  }, [selectedSetId, accountSet?.id, accountSet?._id]);

  useEffect(() => {
    if (pendingMode !== "AI Smart Ladder") {
      setLadderGroupId("");
    }
  }, [pendingMode]);

  useEffect(() => {
    setLadderGroupId("");
  }, [accountSet?.id, accountSet?._id]);

  const fetchLastLadderGroupId = useCallback(async () => {
    const activeAccountSetId = accountSet?.id || accountSet?._id;
    if (!activeAccountSetId) return null;
    try {
      const response = await API.get("/trading/pending-orders", {
        params: { accountSetId: activeAccountSetId, status: "all" },
      });
      const orders = response.data?.orders || [];
      const ladderOrders = orders.filter(
        (order) => order?.pendingMode === "AI Smart Ladder" && order?.ladderGroupId,
      );
      if (!ladderOrders.length) return null;

      const groupMap = new Map();
      for (const order of ladderOrders) {
        const groupId = order.ladderGroupId;
        const createdAtMs = order.createdAt ? new Date(order.createdAt).getTime() : 0;
        const status = order.ladderStepStatus;
        const entry = groupMap.get(groupId) || { latestCreatedAt: 0, hasActiveStep: false };
        entry.latestCreatedAt = Math.max(entry.latestCreatedAt, createdAtMs);
        if (!status || !["COMPLETED_TP", "SKIPPED"].includes(status)) {
          entry.hasActiveStep = true;
        }
        groupMap.set(groupId, entry);
      }

      let selectedGroupId = null;
      let latestCreatedAt = 0;
      for (const [groupId, info] of groupMap.entries()) {
        if (!info.hasActiveStep) continue;
        if (info.latestCreatedAt >= latestCreatedAt) {
          selectedGroupId = groupId;
          latestCreatedAt = info.latestCreatedAt;
        }
      }

      return selectedGroupId;
    } catch {
      return null;
    }
  }, [accountSet?.id, accountSet?._id]);

  useEffect(() => {
    if (pendingMode !== "AI Smart Ladder") return;
    if (ladderGroupId) return;
    let isMounted = true;
    (async () => {
      const lastGroupId = await fetchLastLadderGroupId();
      if (isMounted && lastGroupId) {
        setLadderGroupId(lastGroupId);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [pendingMode, ladderGroupId, fetchLastLadderGroupId]);

  useEffect(() => {
    const locks = executionLocks.current;
    const timeouts = executionTimeouts.current;

    return () => {
      locks.clear();
      timeouts.forEach((timeoutId) => {
        clearTimeout(timeoutId);
      });
      timeouts.clear();
    };
  }, []);

  useEffect(() => {
    setFutureQuote(null);
    setSpotQuote(null);
    setLockedFutureQuote(null);
    setLockedSpotQuote(null);
    setDisplayQuoteSourceLabel("");
    setBuyPremium(0);
    setSellPremium(0);
    setPremiumLive(false);
    setCollectorStale(false);
    setQuoteFeedStatus("");

    if (symbolsLocked && lockedFutureSymbol && lockedSpotSymbol) {
      setSelectedBroker1(lockedFutureSymbol);
      setSelectedBroker2(lockedSpotSymbol);
    } else {
      setSelectedBroker1("");
      setSelectedBroker2("");
    }
  }, [activeAccountSetId, accountSet?.name, symbolsLocked, lockedFutureSymbol, lockedSpotSymbol]);

  const brokers = accountSet?.brokers || [];

  const broker1 = brokers.find((b) => b.position === 1) || brokers[0] || {};
  const broker2 = brokers.find((b) => b.position === 2) || brokers[1] || {};

  const broker1Terminal = broker1.terminal || "";
  const broker2Terminal = broker2.terminal || "";

  const broker1Id = broker1.id || broker1._id || null;
  const broker2Id = broker2.id || broker2._id || null;

  const broker1Label = broker1.brokerName
    ? `${broker1.brokerName} (${broker1Terminal})`
    : `Broker 1 (${broker1Terminal})`;
  const broker2Label = broker2.brokerName
    ? `${broker2.brokerName} (${broker2Terminal})`
    : `Broker 2 (${broker2Terminal})`;
  const ladderGroupShort = ladderGroupId ? ladderGroupId.slice(0, 8) : "";
  const formatNumber = (value, digits = 2) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return "—";
    return num.toLocaleString(undefined, {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    });
  };

  const formatPercent = (value, digits = 1) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return "—";
    return `${num.toFixed(digits)}%`;
  };

  const formatVolume = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return "—";
    return String(num);
  };

  const getDecimalPlaces = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return 0;
    const text = String(num);
    if (text.includes("e-")) {
      const parts = text.split("e-");
      return parts[1] ? parseInt(parts[1], 10) : 0;
    }
    const dotIndex = text.indexOf(".");
    return dotIndex === -1 ? 0 : text.length - dotIndex - 1;
  };

  const isAlignedToStep = (value, step, base = 0) => {
    const valNum = Number(value);
    const stepNum = Number(step);
    const baseNum = Number(base);
    if (!Number.isFinite(valNum) || !Number.isFinite(stepNum) || stepNum <= 0) return true;
    const scale = Math.pow(
      10,
      Math.max(getDecimalPlaces(valNum), getDecimalPlaces(stepNum), getDecimalPlaces(baseNum), 6),
    );
    const scaledValue = Math.round(valNum * scale);
    const scaledBase = Math.round(baseNum * scale);
    const scaledStep = Math.round(stepNum * scale);
    if (scaledStep <= 0) return true;
    return (scaledValue - scaledBase) % scaledStep === 0;
  };

  const validateVolumeForBroker = (volumeValue, brokerInfo) => {
    if (!brokerInfo || !Number.isFinite(volumeValue) || volumeValue <= 0) {
      return { ok: true, message: "" };
    }
    const volumeMin = Number(brokerInfo.volumeMin);
    const volumeStep = Number(brokerInfo.volumeStep);
    const volumeMax = Number(brokerInfo.volumeMax);
    const issues = [];

    if (Number.isFinite(volumeMin) && volumeMin > 0 && volumeValue < volumeMin) {
      issues.push(`min ${formatVolume(volumeMin)}`);
    }
    if (Number.isFinite(volumeMax) && volumeMax > 0 && volumeValue > volumeMax) {
      issues.push(`max ${formatVolume(volumeMax)}`);
    }
    if (Number.isFinite(volumeStep) && volumeStep > 0) {
      const base = Number.isFinite(volumeMin) ? volumeMin : 0;
      if (!isAlignedToStep(volumeValue, volumeStep, base)) {
        issues.push(`step ${formatVolume(volumeStep)}`);
      }
    }

    return {
      ok: issues.length === 0,
      message: issues.join(", "),
    };
  };

  const volumeValue = Number.isFinite(parseFloat(volume)) ? parseFloat(volume) : null;
  const broker1VolumeCheck = validateVolumeForBroker(volumeValue, marginInfo.broker1);
  const broker2VolumeCheck = validateVolumeForBroker(volumeValue, marginInfo.broker2);
  const volumeInvalid = Boolean(volumeValue && (!broker1VolumeCheck.ok || !broker2VolumeCheck.ok));
  const marginBlocked = Boolean(marginInfo.marginInsufficient);
  const lowMarginBrokers = [
    marginInfo.broker1?.insufficient ? broker1Label : "",
    marginInfo.broker2?.insufficient ? broker2Label : "",
  ].filter(Boolean);
  const marginBlockedMessage = marginBlocked
    ? `Order blocked due to low margin on ${lowMarginBrokers.join(" and ") || "one broker"}. Please maintain sufficient margin on both brokers.`
    : "";

  const buildVolumeSpecSummary = (label, brokerInfo, volumeCheck) => {
    if (!brokerInfo || volumeCheck.ok) return "";
    const parts = [];
    if (Number.isFinite(Number(brokerInfo.volumeMin)) && Number(brokerInfo.volumeMin) > 0) {
      parts.push(`min ${formatVolume(brokerInfo.volumeMin)}`);
    }
    if (Number.isFinite(Number(brokerInfo.volumeStep)) && Number(brokerInfo.volumeStep) > 0) {
      parts.push(`step ${formatVolume(brokerInfo.volumeStep)}`);
    }
    if (Number.isFinite(Number(brokerInfo.volumeMax)) && Number(brokerInfo.volumeMax) > 0) {
      parts.push(`max ${formatVolume(brokerInfo.volumeMax)}`);
    }
    return parts.length ? `${label} (${parts.join(", ")})` : label;
  };

  const volumeValidationMessage = volumeInvalid
    ? [
        buildVolumeSpecSummary("Broker 1", marginInfo.broker1, broker1VolumeCheck),
        buildVolumeSpecSummary("Broker 2", marginInfo.broker2, broker2VolumeCheck),
      ]
        .filter(Boolean)
        .join(" | ") || "Invalid volume. Use broker min/step values."
    : "";

  const fetchMarginInfo = useCallback(
    async (volumeParam = 0) => {
      if (!activeAccountSetId) return;
      const requestId = ++marginRequestId.current;

      setMarginInfo((prev) => ({
        ...prev,
        loading: true,
        error: "",
      }));

      try {
        const symbolsOnlyMessage = (msg) =>
          /symbols\s*-?only|collector\s*-?only/i.test(String(msg || ""));
        let response;
        try {
          response = await API.get("/trading/margin-check", {
            params: {
              accountSetId: activeAccountSetId,
              volume: volumeParam,
            },
          });
          if (response.data?.success === false && symbolsOnlyMessage(response.data?.message)) {
            response = await API.get(`/account-sets/${activeAccountSetId}/margin-check`, {
              params: { volume: volumeParam },
            });
          }
        } catch (err) {
          if (symbolsOnlyMessage(err?.response?.data?.message)) {
            response = await API.get(`/account-sets/${activeAccountSetId}/margin-check`, {
              params: { volume: volumeParam },
            });
          } else {
            throw err;
          }
        }

        if (requestId !== marginRequestId.current) return;

        const data = response.data?.data || {};
        setMarginInfo({
          loading: false,
          error: "",
          broker1: data.broker1 || null,
          broker2: data.broker2 || null,
          marginInsufficient: Boolean(data.marginInsufficient),
        });
        setInitialReady(true);
      } catch (err) {
        if (requestId !== marginRequestId.current) return;
        setMarginInfo((prev) => ({
          ...prev,
          loading: false,
          error: err.response?.data?.message || err.message || "Failed to load margin data",
        }));
        setInitialReady(true);
      }
    },
    [activeAccountSetId],
  );

  useEffect(() => {
    if (!activeAccountSetId) return;
    const parsedVolume = parseFloat(volume);
    const normalizedVolume = Number.isFinite(parsedVolume) && parsedVolume > 0 ? parsedVolume : 0;

    if (marginDebounceRef.current) {
      clearTimeout(marginDebounceRef.current);
    }
    marginDebounceRef.current = setTimeout(() => {
      fetchMarginInfo(normalizedVolume);
    }, 300);

    return () => {
      if (marginDebounceRef.current) {
        clearTimeout(marginDebounceRef.current);
      }
    };
  }, [activeAccountSetId, volume, fetchMarginInfo]);

  useEffect(() => {
    if (!activeAccountSetId) return;
    const parsedVolume = parseFloat(volume);
    const normalizedVolume = Number.isFinite(parsedVolume) && parsedVolume > 0 ? parsedVolume : 0;
    const intervalId = setInterval(() => {
      fetchMarginInfo(normalizedVolume);
    }, 15000);

    return () => {
      clearInterval(intervalId);
    };
  }, [activeAccountSetId, volume, fetchMarginInfo]);

  const fetchLatencyData = useCallback(async () => {
    if (!broker1Id || !broker2Id) return;

    try {
      const [broker1Response, broker2Response] = await Promise.all([
        API.get(`/trading/latency/${broker1Id}`),
        API.get(`/trading/latency/${broker2Id}`),
      ]);

      const broker1OrderSend = broker1Response.data.data?.orderSend || {};
      const broker2OrderSend = broker2Response.data.data?.orderSend || {};

      setLatencyData({
        broker1: {
          lastOrderLatency: broker1OrderSend.current || null,
          averageLatency: broker1OrderSend.average || null,
          lastUpdate: new Date(),
        },
        broker2: {
          lastOrderLatency: broker2OrderSend.current || null,
          averageLatency: broker2OrderSend.average || null,
          lastUpdate: new Date(),
        },
      });
    } catch {}
  }, [broker1Id, broker2Id]);

  useEffect(() => {
    if (accountSet && broker1Id && broker2Id) {
      fetchLatencyData();

      const latencyInterval = setInterval(fetchLatencyData, 30000);

      return () => {
        clearInterval(latencyInterval);
      };
    }
  }, [fetchLatencyData, accountSet, broker1Id, broker2Id]);

  useEffect(() => {
    const handleLatencyUpdate = (data) => {
      const { brokerId, latencyType, latency } = data.data || data;

      if (latencyType === "orderSend" && (brokerId === broker1Id || brokerId === broker2Id)) {
        setLatencyData((prev) => {
          const updated = { ...prev };

          if (brokerId === broker1Id) {
            updated.broker1 = {
              ...updated.broker1,
              lastOrderLatency: latency,
              lastUpdate: new Date(),
            };
          } else if (brokerId === broker2Id) {
            updated.broker2 = {
              ...updated.broker2,
              lastOrderLatency: latency,
              lastUpdate: new Date(),
            };
          }

          return updated;
        });
      }
    };

    const unsubscribeLatency = onMessage("latency_update", handleLatencyUpdate);

    return () => {
      unsubscribeLatency();
    };
  }, [broker1Id, broker2Id]);

  useEffect(() => {
    if (futureQuote && spotQuote) {
      setBuyPremium((futureQuote.ask || 0) - (spotQuote.bid || 0));
      setSellPremium((futureQuote.bid || 0) - (spotQuote.ask || 0));
    } else {
      setBuyPremium(0);
      setSellPremium(0);
    }
  }, [futureQuote, spotQuote]);

  const executeOrder = useCallback(
    async (orderType) => {
      if (!selectedBroker1 || !selectedBroker2 || !volume) {
        toast.error("Please select symbols and enter volume");
        return;
      }

      if (!accountSet?.id && !accountSet?._id) {
        toast.error("Please select an account set");
        return;
      }

      const volumeNum = parseFloat(volume);
      if (!Number.isFinite(volumeNum) || volumeNum <= 0) {
        toast.error("Volume must be a positive number");
        return;
      }

      if (volumeInvalid) {
        toast.error(volumeValidationMessage || "Invalid volume. Use broker min/step values.");
        return;
      }

      if (marginBlocked) {
        toast.error(marginBlockedMessage || "Orders cannot be placed due to low margin.");
        return;
      }

      const tradeKey = `${accountSet.id || accountSet._id}_${selectedBroker1}_${selectedBroker2}_${orderType}`;

      if (!validateTradeState(tradeKey)) {
        toast.error("Trade is already being processed or is in pending state");
        return;
      }

      if (!preventDuplicateExecution(tradeKey)) {
        toast.error("Duplicate execution prevented - please wait for current trade to complete");
        return;
      }

      setExecutionState({
        isExecuting: true,
        step: "Initializing execution...",
        startTime: Date.now(),
        broker1Status: "idle",
        broker2Status: "idle",
      });

      if (slowAckTimerRef.current) clearTimeout(slowAckTimerRef.current);
      slowAckTimerRef.current = setTimeout(() => {
        setExecutionState((prev) => ({
          ...prev,
          step:
            orderType === "current"
              ? "Order sent to brokers, awaiting confirmation..."
              : "Pending order request sent...",
        }));
      }, 2000);

      setExecutingTrades((prev) => new Set([...prev, tradeKey]));

      try {
        if (orderType === "current" && !quotesValidForTrading) {
          throw new Error("Quotes are too stale for safe execution. Please wait for fresh quotes.");
        }

        const { takeProfit: takeProfitValue, takeProfitMode: takeProfitModeValue } =
          normalizeTakeProfitSubmission(takeProfit, takeProfitMode, parseFloat);

        const orderData = {
          accountSetId: accountSet.id || accountSet._id,
          direction,
          volume: volumeNum,
          takeProfit: takeProfitValue,
          takeProfitMode: takeProfitModeValue,
          stopLoss: null,
          scalpingMode: scalping,
          comment: "Premix Trade",
          pendingMode,
          ladderGroupId: pendingMode === "AI Smart Ladder" ? ladderGroupId || null : null,
        };

        let result;
        let successMessage;

        if (orderType === "current") {
          setExecutionState((prev) => ({
            ...prev,
            step: "Executing at current premium with paired execution...",
          }));

          if (!API.defaults.baseURL) {
            throw new Error(
              "API base URL is not configured. Please check your backend connection.",
            );
          }

          const response = await API.post("/trading/execute-current", orderData, {
            timeout: 60000,
          });
          result = { success: response.data?.success, data: response.data };
          successMessage = "Trade executed successfully at current premium on both brokers!";
        } else if (orderType === "target") {
          if (!targetPremium) {
            throw new Error("Please enter target premium");
          }

          orderData.targetPremium = parseFloat(targetPremium);
          setExecutionState((prev) => ({
            ...prev,
            step: "Creating pending order at target premium...",
          }));

          const response = await API.post("/trading/execute-target", orderData, { timeout: 60000 });
          if (pendingMode === "AI Smart Ladder") {
            const createdGroupId = response.data?.pendingOrder?.ladderGroupId;
            if (createdGroupId) {
              setLadderGroupId(createdGroupId);
            }
          }
          result = { success: response.data?.success, data: response.data };
          successMessage = "Pending order created successfully";
        }

        if (result.success) {
          setVolume("");
          setTargetPremium("");
          setTakeProfit("");

          setExecutionState((prev) => ({
            ...prev,
            step: successMessage,
            broker1Status: "success",
            broker2Status: "success",
          }));

          setTimeout(() => {
            toast.success(successMessage);
          }, 500);

          try {
            playOrderFilled();
          } catch {
            void 0;
          }

          fetchLatencyData().catch((latencyErr) => {
            void latencyErr;
          });
        } else {
          throw new Error(result.message || "Execution failed");
        }
      } catch (err) {
        console.error("❌ Enhanced order execution error:", err);

        if (isMarketPausePayload(err)) {
          showMarketPauseWarning();
          clearExecutionTimeout(tradeKey);
          return;
        }

        let errorMessage;

        if (err.code === "ECONNABORTED" || err.message.includes("timeout")) {
          errorMessage =
            "Trade execution timed out. Please check your broker connections and try again.";
        } else if (err.response?.status === 409) {
          errorMessage =
            err.response?.data?.message ||
            "A similar order is already being processed. Please wait.";
        } else if (err.message && err.message.includes("Invalid URL")) {
          const backendPort = process.env.REACT_APP_BACKEND_PORT || "5000";
          errorMessage = `Backend connection error: Invalid URL. Please check if the backend server is running on localhost:${backendPort}`;
        } else if (err.code === "ERR_NETWORK") {
          errorMessage =
            "Network error: Cannot connect to backend server. Please check if the server is running.";
        } else if (err.response?.status === 401) {
          errorMessage = "Authentication failed. Please log in again.";
        } else if (err.response?.status >= 500) {
          errorMessage = "Server error. Please try again in a moment.";
        } else {
          errorMessage = err.response?.data?.message || err.message || "Order execution failed";
        }

        toast.error(errorMessage);
        try {
          playError();
        } catch {}

        setExecutionState((prev) => ({
          ...prev,
          step: `Error: ${errorMessage}`,
          broker1Status: "failed",
          broker2Status: "failed",
        }));

        clearExecutionTimeout(tradeKey);
      } finally {
        if (slowAckTimerRef.current) {
          clearTimeout(slowAckTimerRef.current);
          slowAckTimerRef.current = null;
        }
        setExecutionState((prev) => ({
          ...prev,
          isExecuting: false,
          step: "",
          startTime: null,
          broker1Status: "idle",
          broker2Status: "idle",
        }));

        releaseExecutionLock(tradeKey);
        clearExecutionTimeout(tradeKey);
        setExecutingTrades((prev) => {
          const newSet = new Set(prev);
          newSet.delete(tradeKey);
          return newSet;
        });

        try {
          setTimeout(async () => {
            await fetchLatencyData();
          }, 1000);
        } catch {
          void 0;
        }
      }
    },
    [
      selectedBroker1,
      selectedBroker2,
      volume,
      direction,
      targetPremium,
      takeProfit,
      takeProfitMode,
      scalping,
      pendingMode,
      ladderGroupId,
      accountSet,
      quotesValidForTrading,
      volumeInvalid,
      volumeValidationMessage,
      marginBlocked,
      marginBlockedMessage,
      validateTradeState,
      preventDuplicateExecution,
      releaseExecutionLock,
      clearExecutionTimeout,
      fetchLatencyData,
      toast,
      isMarketPausePayload,
      showMarketPauseWarning,
    ],
  );

  useEffect(() => {
    if (!activeAccountSetId || !selectedBroker1 || !selectedBroker2) {
      return;
    }

    const handleQuoteUpdate = (data) => {
      const incomingSetId = data?.accountSetId;
      if (incomingSetId && String(incomingSetId) !== String(activeAccountSetId)) return;

      const incomingSource = normalizeQuoteSource(
        data?.futureQuote?.source || data?.spotQuote?.source || data?.source,
      );
      if (
        incomingSource === "db" &&
        lastCollectorQuoteAt.current > 0 &&
        Date.now() - lastCollectorQuoteAt.current < COLLECTOR_PREFER_MS
      ) {
        return;
      }
      if (incomingSource === "collector") {
        lastCollectorQuoteAt.current = Date.now();
      }
      const nextSourceLabel = deriveQuoteSourceLabel(data);
      if (nextSourceLabel) {
        setDisplayQuoteSourceLabel((prev) => (prev === nextSourceLabel ? prev : nextSourceLabel));
      }
      if (isMarketPausePayload(data)) {
        showMarketPauseWarning();
        setQuoteFeedStatus("");
      } else if (data?.collectorStale || data?.quoteLive === false) {
        marketPauseToastShownRef.current = false;
        setQuoteFeedStatus("Market closed / stale quotes");
      } else {
        marketPauseToastShownRef.current = false;
        setQuoteFeedStatus("");
      }

      if (symbolsLocked) {
        if (data.futureSymbol === lockedFutureSymbol && data.futureQuote) {
          setFutureQuote(data.futureQuote);
          setLockedFutureQuote(data.futureQuote);
        }

        if (data.spotSymbol === lockedSpotSymbol && data.spotQuote) {
          setSpotQuote(data.spotQuote);
          setLockedSpotQuote(data.spotQuote);
        }
      } else {
        if (data.futureSymbol === selectedBroker1 && data.futureQuote) {
          setFutureQuote(data.futureQuote);
        }
        if (data.spotSymbol === selectedBroker2 && data.spotQuote) {
          setSpotQuote(data.spotQuote);
        }
      }
    };

    const handleQuoteBatch = (quoteUpdates) => {
      Object.values(quoteUpdates).forEach((data) => {
        handleQuoteUpdate(data);
      });
    };

    const handlePremiumUpdate = (data) => {
      if (String(data.accountSetId || "") === String(activeAccountSetId || "")) {
        const nextSourceLabel = deriveQuoteSourceLabel(data);
        if (nextSourceLabel) {
          setDisplayQuoteSourceLabel((prev) => (prev === nextSourceLabel ? prev : nextSourceLabel));
        }
        if (isMarketPausePayload(data)) {
          showMarketPauseWarning();
          setQuoteFeedStatus("");
        } else if (data?.collectorStale || data?.quoteLive === false) {
          marketPauseToastShownRef.current = false;
          setQuoteFeedStatus("Market closed / stale quotes");
        } else {
          marketPauseToastShownRef.current = false;
          setQuoteFeedStatus("");
        }
        setBuyPremium(data.buyPremium || 0);
        setSellPremium(data.sellPremium || 0);
        setPremiumLive(Boolean(data.premiumLive));
        setCollectorStale(Boolean(data.collectorStale));

        if (data.futureQuote) {
          setFutureQuote({
            ...data.futureQuote,
            timestamp: data.timestamp || new Date(),
            source: data.source || "websocket",
          });
        }
        if (data.spotQuote) {
          setSpotQuote({
            ...data.spotQuote,
            timestamp: data.timestamp || new Date(),
            source: data.source || "websocket",
          });
        }

        setQuotesValidForTrading(
          validateTradeQuotes(
            data.futureQuote ? { ...data.futureQuote, timestamp: data.timestamp } : null,
            data.spotQuote ? { ...data.spotQuote, timestamp: data.timestamp } : null,
          ),
        );
      }
    };

    const handleConnectionConfirmed = () => {};

    const handleSubscriptionConfirmed = () => {};

    const handleApiError = (data) => {
      if (isMarketPausePayload(data)) {
        showMarketPauseWarning();
        return;
      }
      if (isCollectorAvailabilityNotice(data?.message)) {
        setQuoteFeedStatus(getCollectorAvailabilityLabel(data?.message));
        return;
      }
      console.error("🚨 TradeExecution: API Error received:", data);
      toast.error(`API Error: ${data.message || "Unknown error"}`);
    };

    connectWS(activeAccountSetId);

    if (symbolsLocked) {
      subscribeToQuotes(
        activeAccountSetId,
        lockedFutureSymbol,
        lockedSpotSymbol,
        handleQuoteUpdate,
      );
      subscribeToPremium(activeAccountSetId, lockedFutureSymbol, lockedSpotSymbol);
    } else if (selectedBroker1 && selectedBroker2) {
      subscribeToQuotes(activeAccountSetId, selectedBroker1, selectedBroker2, handleQuoteUpdate);
      subscribeToPremium(activeAccountSetId, selectedBroker1, selectedBroker2);
    }

    const unsubQuote = onMessage("quote_update", handleQuoteUpdate);
    const unsubPremium = onMessage("premium_update", handlePremiumUpdate);
    const unsubConnection = onMessage("connection", handleConnectionConfirmed);
    const unsubSubscription = onMessage("subscription_confirmed", handleSubscriptionConfirmed);
    const unsubApiError = onMessage("api_error", handleApiError);

    const unsubQuoteBatch = onQuoteBatch(handleQuoteBatch);

    const unsubProfit = onMessage("order_profit_batch", (msg) => {
      const asid = msg?.accountSetId;
      if (asid && asid !== activeAccountSetId) return;
      const updates = msg.data || msg;
      if (!Array.isArray(updates)) return;
      for (const u of updates) {
        const profit = Number(u.profit) || 0;
        const ticket = String(u.ticket);
        const pos = Number(u.brokerPosition);
        if (pos === 1) b1ProfitRef.current.set(ticket, profit);
        else if (pos === 2) b2ProfitRef.current.set(ticket, profit);
      }
      let b1 = 0,
        b2 = 0;
      b1ProfitRef.current.forEach((v) => {
        b1 += Number(v) || 0;
      });
      b2ProfitRef.current.forEach((v) => {
        b2 += Number(v) || 0;
      });
      setRealTimeProfits({ broker1: b1, broker2: b2, total: b1 + b2 });
    });

    return () => {
      unsubQuote();
      unsubPremium();
      unsubConnection();
      unsubSubscription();
      unsubApiError();
      unsubQuoteBatch();
      unsubProfit();
    };
  }, [
    selectedBroker1,
    selectedBroker2,
    activeAccountSetId,
    symbolsLocked,
    lockedFutureSymbol,
    lockedSpotSymbol,
    toast,
    deriveQuoteSourceLabel,
    normalizeQuoteSource,
    isMarketPausePayload,
    showMarketPauseWarning,
  ]);

  useEffect(() => {
    const activeQuotes = symbolsLocked
      ? { future: lockedFutureQuote || futureQuote, spot: lockedSpotQuote || spotQuote }
      : { future: futureQuote, spot: spotQuote };

    if (activeQuotes.future && activeQuotes.spot) {
      const futureValid = isQuoteFresh(activeQuotes.future);
      const spotValid = isQuoteFresh(activeQuotes.spot);

      if (futureValid && spotValid) {
        const calculatedBuyPremium = (activeQuotes.future.ask || 0) - (activeQuotes.spot.bid || 0);
        const calculatedSellPremium = (activeQuotes.future.bid || 0) - (activeQuotes.spot.ask || 0);

        setBuyPremium(calculatedBuyPremium);
        setSellPremium(calculatedSellPremium);

        const validForTrading = validateTradeQuotes(activeQuotes.future, activeQuotes.spot);
        setQuotesValidForTrading(validForTrading);
      } else {
        setQuotesValidForTrading(false);
      }
    } else {
      setBuyPremium(0);
      setSellPremium(0);
      setQuotesValidForTrading(false);
    }
  }, [futureQuote, spotQuote, lockedFutureQuote, lockedSpotQuote, symbolsLocked]);

  const getLatencyToneClass = (value) => {
    const latency = Number(value);
    if (!Number.isFinite(latency)) return "admin-badge-neutral";
    if (latency <= 120) return "admin-badge-success";
    if (latency <= 250) return "admin-badge-warning";
    return "admin-badge-danger";
  };

  const formatLatencyValue = (value) => {
    const latency = Number(value);
    return Number.isFinite(latency) && latency > 0 ? `${Math.round(latency)}ms` : "—";
  };

  const getEffectiveAvg = (avg, last) => {
    const a = Number(avg);
    if (Number.isFinite(a) && a > 0) return a;
    const l = Number(last);
    return Number.isFinite(l) && l > 0 ? l : null;
  };

  const accountSetOptions = accountSets.map((set) => ({
    value: set._id || set.id,
    label: set.name,
  }));
  const hasAccountSet = Boolean(accountSet);
  const premiumToneClass = premiumLive
    ? "admin-badge-success"
    : collectorStale
      ? "admin-badge-warning"
      : "admin-badge-neutral";
  const premiumStatusLabel = premiumLive ? "Live" : collectorStale ? "Stale" : "Waiting";
  const currentPremiumBadge = premiumLive
    ? (direction === "Buy" ? buyPremium : sellPremium).toFixed(2)
    : "Waiting";
  const targetPremiumBadge = String(targetPremium || "").trim();
  const hasTargetPremium = targetPremiumBadge.length > 0;
  const latencyUpdatedLabel =
    [
      latencyData.broker1.lastUpdate ? `B1 ${formatAppTime(latencyData.broker1.lastUpdate)}` : "",
      latencyData.broker2.lastUpdate ? `B2 ${formatAppTime(latencyData.broker2.lastUpdate)}` : "",
    ]
      .filter(Boolean)
      .join(" · ") || "Latency updates after live executions";

  if (!initialReady) {
    return (
      <AppPageLoader
        label="Loading Trade Execution"
        detail="Fetching margin data and preparing execution controls..."
      />
    );
  }

  return (
    <div className="admin-page admin-broadcast-page trade-execution-page">
      <div className="admin-broadcast-header">
        <div className="admin-broadcast-header-copy">
          <h1>
            <BarChart3 className="admin-broadcast-header-icon" />
            <span>Trade Execution</span>
          </h1>
          <p>
            Place cross-broker orders with live premium checks, margin validation, and a cleaner
            execution workflow.
          </p>
        </div>
      </div>

      <div className="te-perf-grid">
        <div className="te-perf-card">
          <div className="te-perf-card-top">
            <div className="te-perf-heading-group">
              <span className="te-perf-title">
                {hasAccountSet
                  ? accountSet.futureSymbol && accountSet.spotSymbol
                    ? `${accountSet.futureSymbol} / ${accountSet.spotSymbol}`
                    : accountSet.name
                  : "No Set Selected"}
              </span>
              <div className="te-perf-subtitle-row">
                <span className="te-perf-subtitle">
                  {hasAccountSet ? accountSet.name : "Select a workspace to begin"}
                </span>
              </div>
            </div>
            <div className="te-perf-icon">
              <Settings size={20} />
            </div>
          </div>
          <div className="te-perf-detail-list">
            <div className="te-perf-detail-item">
              <span className="te-perf-detail-label">Type</span>
              <span className="te-perf-detail-value">{broker1Terminal || "—"}</span>
            </div>
            <div className="te-perf-detail-item">
              <span className="te-perf-detail-label">Type</span>
              <span className="te-perf-detail-value">{broker2Terminal || "—"}</span>
            </div>
            <div className="te-perf-detail-item">
              <span className="te-perf-detail-label">Account</span>
              <span className="te-perf-detail-value">{broker1.accountNumber || "—"}</span>
            </div>
            <div className="te-perf-detail-item">
              <span className="te-perf-detail-label">Account</span>
              <span className="te-perf-detail-value">{broker2.accountNumber || "—"}</span>
            </div>
          </div>
        </div>

        <div className="te-perf-card">
          <div className="te-perf-card-top">
            <div className="te-perf-heading-group">
              <span className="te-perf-title">Execution Latency</span>
              <div className="te-perf-subtitle-row">
                <span className="te-perf-subtitle">{latencyUpdatedLabel}</span>
              </div>
            </div>
            <div className="te-perf-icon">
              <Zap size={20} />
            </div>
          </div>
          <div className="te-perf-detail-list">
            <div className="te-perf-detail-item">
              <span className="te-perf-detail-label">B1 Last</span>
              <span
                className={`te-perf-detail-value ${getLatencyToneClass(latencyData.broker1.lastOrderLatency).replace("admin-badge-", "te-perf-tone-")}`}
              >
                {formatLatencyValue(latencyData.broker1.lastOrderLatency)}
              </span>
            </div>
            <div className="te-perf-detail-item">
              <span className="te-perf-detail-label">B2 Last</span>
              <span
                className={`te-perf-detail-value ${getLatencyToneClass(latencyData.broker2.lastOrderLatency).replace("admin-badge-", "te-perf-tone-")}`}
              >
                {formatLatencyValue(latencyData.broker2.lastOrderLatency)}
              </span>
            </div>
            <div className="te-perf-detail-item">
              <span className="te-perf-detail-label">B1 Avg</span>
              <span
                className={`te-perf-detail-value ${getLatencyToneClass(getEffectiveAvg(latencyData.broker1.averageLatency, latencyData.broker1.lastOrderLatency)).replace("admin-badge-", "te-perf-tone-")}`}
              >
                {formatLatencyValue(
                  getEffectiveAvg(
                    latencyData.broker1.averageLatency,
                    latencyData.broker1.lastOrderLatency,
                  ),
                )}
              </span>
            </div>
            <div className="te-perf-detail-item">
              <span className="te-perf-detail-label">B2 Avg</span>
              <span
                className={`te-perf-detail-value ${getLatencyToneClass(getEffectiveAvg(latencyData.broker2.averageLatency, latencyData.broker2.lastOrderLatency)).replace("admin-badge-", "te-perf-tone-")}`}
              >
                {formatLatencyValue(
                  getEffectiveAvg(
                    latencyData.broker2.averageLatency,
                    latencyData.broker2.lastOrderLatency,
                  ),
                )}
              </span>
            </div>
          </div>
        </div>

        <div className="te-perf-card">
          <div className="te-perf-card-top">
            <div className="te-perf-heading-group">
              <span className="te-perf-title">Live Premium</span>
              <div className="te-perf-subtitle-row">
                <span className="te-perf-subtitle">
                  {quoteFeedStatus ||
                    (displayQuoteSourceLabel
                      ? `Source: ${displayQuoteSourceLabel}`
                      : "Awaiting feed")}
                </span>
              </div>
            </div>
            <div className="te-perf-icon">
              <TrendingUp size={20} />
            </div>
          </div>
          <div className="te-perf-detail-list">
            <div className="te-perf-detail-item">
              <span className="te-perf-detail-label">Sell Premium</span>
              <span
                className={`te-perf-detail-value ${sellPremium > 0 ? "te-perf-tone-success" : sellPremium < 0 ? "te-perf-tone-danger" : "te-perf-tone-neutral"}`}
              >
                {sellPremium !== 0 ? sellPremium.toFixed(2) : "—"}
              </span>
            </div>
            <div className="te-perf-detail-item">
              <span className="te-perf-detail-label">Buy Premium</span>
              <span
                className={`te-perf-detail-value ${buyPremium > 0 ? "te-perf-tone-success" : buyPremium < 0 ? "te-perf-tone-danger" : "te-perf-tone-neutral"}`}
              >
                {buyPremium !== 0 ? buyPremium.toFixed(2) : "—"}
              </span>
            </div>
            <div className="te-perf-detail-item">
              <span className="te-perf-detail-label">Feed Status</span>
              <span
                className={`te-perf-detail-value ${premiumToneClass.replace("admin-badge-", "te-perf-tone-")}`}
              >
                {premiumStatusLabel}
              </span>
            </div>
            <div className="te-perf-detail-item">
              <span className="te-perf-detail-label">Target Premium</span>
              <span className="te-perf-detail-value te-perf-tone-neutral">
                {targetPremium ? Number(targetPremium).toFixed(4) : "—"}
              </span>
            </div>
          </div>
        </div>

        <div className="te-perf-card">
          <div className="te-perf-card-top">
            <div className="te-perf-heading-group">
              <span className="te-perf-title">Open P&amp;L</span>
              <div className="te-perf-subtitle-row">
                <span className="te-perf-subtitle">Live open positions</span>
              </div>
            </div>
            <div className="te-perf-icon">
              <Activity size={20} />
            </div>
          </div>
          <div className="te-perf-detail-list">
            <div className="te-perf-detail-item">
              <span className="te-perf-detail-label">B1 P&L</span>
              <span
                className={`te-perf-detail-value ${realTimeProfits.broker1 > 0 ? "te-perf-tone-success" : realTimeProfits.broker1 < 0 ? "te-perf-tone-danger" : "te-perf-tone-neutral"}`}
              >
                {realTimeProfits.broker1 !== 0
                  ? (realTimeProfits.broker1 > 0 ? "+" : "") + realTimeProfits.broker1.toFixed(2)
                  : "—"}
              </span>
            </div>
            <div className="te-perf-detail-item">
              <span className="te-perf-detail-label">B2 P&L</span>
              <span
                className={`te-perf-detail-value ${realTimeProfits.broker2 > 0 ? "te-perf-tone-success" : realTimeProfits.broker2 < 0 ? "te-perf-tone-danger" : "te-perf-tone-neutral"}`}
              >
                {realTimeProfits.broker2 !== 0
                  ? (realTimeProfits.broker2 > 0 ? "+" : "") + realTimeProfits.broker2.toFixed(2)
                  : "—"}
              </span>
            </div>
            <div className="te-perf-detail-item" style={{ gridColumn: "span 2" }}>
              <span className="te-perf-detail-label">Combined</span>
              <span
                className={`te-perf-detail-value te-perf-pnl-total ${realTimeProfits.total > 0 ? "te-perf-tone-success" : realTimeProfits.total < 0 ? "te-perf-tone-danger" : "te-perf-tone-neutral"}`}
              >
                {realTimeProfits.total !== 0
                  ? (realTimeProfits.total > 0 ? "+" : "") + realTimeProfits.total.toFixed(2)
                  : "—"}
              </span>
            </div>
          </div>
        </div>
      </div>

      <section className="admin-card admin-broadcast-card">
        <div className="admin-card-heading">
          <div className="admin-broadcast-card-copy">
            <div className="admin-card-title">
              <Target />
              <span>Execution Workspace</span>
            </div>
            <p>
              Configure the symbol pair, trade direction, and order settings before sending the
              request to both brokers.
            </p>
          </div>
          <span
            className={`admin-badge ${quotesValidForTrading ? "admin-badge-success" : "admin-badge-neutral"}`}
          >
            Quotes {quotesValidForTrading ? "Ready" : "Waiting"}
          </span>
        </div>

        <div className="trade-exec-two-col">
          <div className="te-panel">
            <div className="te-panel-header">
              <div className="admin-card-title te-panel-title">
                <Settings />
                <span>Trade Setup</span>
              </div>
              <p>Select the account, symbol pair, and execution direction.</p>
            </div>

            <div className="te-fields-row cols-2">
              <div className="admin-field-group">
                <label className="admin-label">Account Set</label>
                <CustomSelect
                  value={selectedSetId}
                  onChange={(value) => {
                    if (typeof onAccountSetChange === "function") onAccountSetChange(value);
                  }}
                  options={accountSetOptions}
                  placeholder="Select account set"
                  disabled={!accountSets.length || typeof onAccountSetChange !== "function"}
                />
              </div>
              <div className="admin-field-group">
                <label className="admin-label">Volume</label>
                <input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={volume}
                  onChange={(e) => setVolume(e.target.value)}
                  onBlur={() => {
                    const n = parseFloat(volume);
                    if (Number.isFinite(n)) setVolume(String(Math.round(n * 100) / 100));
                  }}
                  className="admin-input te-no-spin"
                />
              </div>
            </div>

            {volumeInvalid && (
              <div className="te-alert-danger">
                <AlertTriangle size={15} />
                <span>
                  Invalid volume. {volumeValidationMessage || "Use broker min/step values."}
                </span>
              </div>
            )}

            {marginInfo.error && (
              <div className="te-alert-danger">
                <AlertTriangle size={15} />
                <span>{marginInfo.error}</span>
              </div>
            )}
            {marginBlocked && (
              <div className="te-alert-danger">
                <AlertTriangle size={15} />
                <span>{marginBlockedMessage || "Orders cannot be placed due to low margin."}</span>
              </div>
            )}

            <div className="te-margin-grid">
              {[
                { key: "broker1", label: broker1Label, data: marginInfo.broker1 },
                { key: "broker2", label: broker2Label, data: marginInfo.broker2 },
              ].map(({ key, label, data }) => (
                <div className="te-margin-card" key={key}>
                  <div className="te-margin-card-header">
                    <div className="te-margin-broker-label">
                      <Landmark size={14} />
                      {label}
                    </div>
                    {marginInfo.loading && <span className="te-margin-inline-loader" />}
                  </div>

                  <div
                    className={`te-margin-hero ${data?.insufficient ? "te-margin-hero--danger" : "te-margin-hero--success"}`}
                  >
                    <span className="te-margin-hero-label">Required Margin</span>
                    <span className="te-margin-hero-value">
                      <ShieldCheck size={18} />
                      {volumeValue ? formatNumber(data?.requiredMargin) : "--"}
                    </span>
                  </div>

                  <div className="te-margin-metrics-grid">
                    <div className="te-margin-tile">
                      <span className="te-margin-tile-label">Balance</span>
                      <span
                        className={`te-margin-tile-value ${Number(data?.balance) < 0 ? "te-margin-tone-danger" : Number(data?.balance) > 0 ? "te-margin-tone-success" : ""}`}
                      >
                        <Wallet size={14} />
                        {formatNumber(data?.balance)}
                      </span>
                    </div>
                    <div className="te-margin-tile">
                      <span className="te-margin-tile-label">Equity</span>
                      <span className="te-margin-tile-value">
                        <CreditCard size={14} />
                        {formatNumber(data?.equity)}
                      </span>
                    </div>
                    <div className="te-margin-tile">
                      <span className="te-margin-tile-label">Margin</span>
                      <span className="te-margin-tile-value">
                        <ShieldCheck size={14} />
                        {formatNumber(data?.margin)}
                      </span>
                    </div>
                    <div className="te-margin-tile">
                      <span className="te-margin-tile-label">Free Margin</span>
                      <span className="te-margin-tile-value">
                        <Unlock size={14} />
                        {formatNumber(data?.freeMargin)}
                      </span>
                    </div>
                    <div className="te-margin-tile">
                      <span className="te-margin-tile-label">Margin Level</span>
                      <span className="te-margin-tile-value">
                        <Gauge size={14} />
                        {formatPercent(data?.marginLevel)}
                      </span>
                    </div>
                    <div className="te-margin-tile">
                      <span className="te-margin-tile-label">Min / Step</span>
                      <span className="te-margin-tile-value">
                        <Ruler size={14} />
                        {formatVolume(data?.volumeMin)} / {formatVolume(data?.volumeStep)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="te-panel">
            <div className="te-panel-header">
              <div className="admin-card-title te-panel-title">
                <Zap />
                <span>Order Details</span>
              </div>
              <p>Set volume, premium targets, and execution options.</p>
            </div>

            <div className="admin-field-group">
              <label className="admin-label">Take Profit</label>
              <RadioButtonGroup
                name="takeProfitMode"
                value={takeProfitMode}
                onChange={(mode) => {
                  setTakeProfitMode(mode);
                  if (mode === "None") setTakeProfit("");
                }}
                options={[
                  { value: "None", label: "None", tone: "danger" },
                  { value: "Premium", label: "Premium", tone: "default" },
                  { value: "Amount", label: "Amount", tone: "success" },
                ]}
              />
            </div>

            <div className="te-fields-row cols-2">
              <div className="admin-field-group">
                <label className="admin-label">Target Premium</label>
                <input
                  type="number"
                  step="0.01"
                  placeholder="e.g. 28.00"
                  value={targetPremium}
                  onChange={(e) => setTargetPremium(e.target.value)}
                  onBlur={() => {
                    const n = parseFloat(targetPremium);
                    if (Number.isFinite(n)) setTargetPremium(String(Math.round(n * 100) / 100));
                  }}
                  className="admin-input te-no-spin"
                />
              </div>
              <div className="admin-field-group">
                <label className="admin-label">
                  {takeProfitMode === "Amount" ? "Profit Target ($)" : "Deficit Premium"}
                </label>
                <input
                  type="number"
                  step="0.01"
                  placeholder={takeProfitMode === "Amount" ? "e.g. 50.00" : "e.g. 5.00"}
                  value={takeProfit}
                  onChange={(e) => setTakeProfit(e.target.value)}
                  onBlur={() => {
                    const n = parseFloat(takeProfit);
                    if (Number.isFinite(n)) setTakeProfit(String(Math.round(n * 100) / 100));
                  }}
                  disabled={takeProfitMode === "None"}
                  className={`admin-input te-no-spin${takeProfitMode === "None" ? " te-input-disabled" : ""}`}
                />
              </div>
            </div>

            <div className="te-toggle-row">
              <div>
                <div className="te-toggle-label">Scalping Mode</div>
                <div className="te-toggle-sub">
                  Requires 3 consecutive fresh quote confirmations before triggering TP close or
                  order execution.
                </div>
              </div>
              <label className="te-toggle-pill">
                <input
                  type="checkbox"
                  checked={scalping}
                  onChange={(e) => setScalping(e.target.checked)}
                />
                <span className="te-toggle-track" />
                <span className="te-toggle-knob" />
              </label>
            </div>

            <div className="admin-field-group">
              <div className="te-label-with-meta">
                <label className="admin-label">Pending Mode</label>
                {pendingMode === "AI Smart Ladder" && ladderGroupShort && (
                  <span className="te-label-meta" title={ladderGroupId}>
                    Group: <strong>{ladderGroupShort}</strong>
                  </span>
                )}
              </div>
              <RadioButtonGroup
                name="pendingMode"
                value={pendingMode}
                onChange={(mode) => setPendingMode(mode)}
                options={[
                  { value: "Classic Trigger", label: "Classic Trigger", tone: "default" },
                  { value: "AI Smart Ladder", label: "AI Smart Ladder", tone: "success" },
                ]}
              />
            </div>

            {pendingMode === "Classic Trigger" && (
              <div className="te-alert-info">
                <Info size={16} style={{ flexShrink: 0, marginTop: 2 }} />
                <span>
                  Each order monitors premium independently. As soon as the target is hit, the order
                  executes. No sequence, no dependency. Whichever order meets its condition first,
                  goes first.
                </span>
              </div>
            )}

            {pendingMode === "AI Smart Ladder" && (
              <div className="te-alert-success">
                <Info size={16} style={{ flexShrink: 0, marginTop: 2 }} />
                <span>
                  Orders execute in a strict sequence within a group. Only the current eligible step
                  can trigger. The rest stay locked until the previous trade closes. Take Profit is
                  required. If set to None, it defaults to Premium mode.
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="te-action-row">
          <div className="te-action-panel">
            <div className="te-direction-group">
              <button
                type="button"
                className={`te-direction-btn te-direction-buy${direction === "Buy" ? " te-direction-active" : ""}`}
                onClick={() => setDirection("Buy")}
              >
                {direction === "Buy" && <span className="te-direction-pill">Selected</span>}
                <TrendingUp size={26} />
                <span>Buy</span>
              </button>
              <button
                type="button"
                className={`te-direction-btn te-direction-sell${direction === "Sell" ? " te-direction-active" : ""}`}
                onClick={() => setDirection("Sell")}
              >
                {direction === "Sell" && <span className="te-direction-pill">Selected</span>}
                <TrendingDown size={26} />
                <span>Sell</span>
              </button>
            </div>
          </div>
          <div className="te-action-panel">
            <div className="te-execute-group">
              <button
                type="button"
                className="admin-btn te-exec-btn te-exec-btn-current"
                onClick={() => executeOrder("current")}
                disabled={executionState.isExecuting || hasTargetPremium}
                title={
                  hasTargetPremium
                    ? "Clear target premium to use current premium execution."
                    : undefined
                }
              >
                <span className="te-exec-btn-live-badge">{currentPremiumBadge}</span>
                Execute at Current Premium
              </button>
              <button
                type="button"
                className="admin-btn te-exec-btn te-exec-btn-target"
                onClick={() => executeOrder("target")}
                disabled={executionState.isExecuting}
              >
                {targetPremiumBadge && (
                  <span className="te-exec-btn-live-badge te-exec-btn-live-badge-target">
                    {targetPremiumBadge}
                  </span>
                )}
                Execute at Target Premium
              </button>
            </div>
          </div>
        </div>

        {executionState.isExecuting &&
          (() => {
            const b1 = executionState.broker1Status;
            const b2 = executionState.broker2Status;
            const statusWord = (s) =>
              s === "success"
                ? "completed"
                : s === "failed"
                  ? "failed"
                  : s === "pending"
                    ? "processing"
                    : s === "rollback"
                      ? "rolling back"
                      : "waiting";
            const elapsed = executionState.startTime
              ? Math.floor((Date.now() - executionState.startTime) / 1000)
              : 0;
            return (
              <div className="te-alert-info te-alert-info-exec">
                <div className="te-spin" />
                <span>
                  {executionState.step} {broker1Label} is {statusWord(b1)}, {broker2Label} is{" "}
                  {statusWord(b2)}.
                </span>
                {elapsed > 0 && <span className="admin-badge admin-badge-info">{elapsed}s</span>}
              </div>
            );
          })()}
      </section>
    </div>
  );
}
